/**
 * Form16_WellnessPreventiveAssessment.jsx
 * Collection: wellness_assessments
 * Redirect: /dashboard  (end of form sequence)
 *
 * Source: Wellness_Preventive_Assesssment.pdf
 * Sections: PHQ-9 depression screen, cognitive/appearance, hearing, fall risk,
 * functional ADLs (items 4–11), general health (pain/exercise/stress/tobacco/alcohol),
 * preventive care (immunizations, screenings, labs, vitals, CPAP/O2).
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/wellness-assessment";
const NEXT    = "/dashboard";

const DEPRESSION_LEVELS = [
  "No depression",
  "Minimal (1–4)",
  "Mild (5–9)",
  "Moderate (10–14)",
  "Moderately Severe (15–19)",
  "Severe (20–27)",
];

// Items 6–11 follow the pattern: Yes/No + optional explain
const ADL_ITEMS = [
  [6,  "Need help with medication"],
  [7,  "Need help with transportation"],
  [8,  "Need help with shopping"],
  [9,  "Need help with preparing and eating meals"],
  [10, "Able to bathe and dress independently"],
  [11, "Urine Incontinence"],
];

export default function WellnessPreventiveAssessment({
  patientName = "", patientAge = "", patientGender = "", onSubmit,
}) {
  const history   = useHistory();
  const formRef    = useRef(null);
  const sigRef     = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    // 1. PHQ-9
    phq9Date:          today,
    phq9Score:         "",
    phq9Level:         "",

    // 1b. Cognitive
    cogAlert:          "",
    cogOrientedPlace:  "",
    cogOrientedTime:   "",
    cogOrientedPerson: "",

    // 1c. Appearance
    appearNoDistress:    "",
    appearAffectNormal:  "",
    appearFunctional:    "",
    appearLevelOfSafety: "",

    // 2. Hearing
    hearingImpaired:   "",
    usesHearingAid:    "",

    // 3. Fall risk
    fallsLastYear:     "",
    afraidOfFalling:   "",

    // 4. Transfers
    transfersIndependent: "",
    transfersExplain:     "",

    // 5. Home safety
    homeSafetyRug:         "",
    homeSafetyBathroomBars: "",
    homeSafetyStairBars:   "",

    // 6–11 ADLs
    adl: Object.fromEntries(
      ADL_ITEMS.map(([n]) => [`q${n}`, { answer: "", explain: "" }])
    ),

    // 12. General health
    painScreening:    "",
    exerciseLevel:    "",
    exerciseDaysPerWeek: "",
    stressLevel:      "",
    tobaccoUse:       "",
    secondhandSmoke:  false,
    alcoholMisuse:    "",
    alcoholDetail:    "",

    // Preventive care
    dailyAspirin:       "",
    immunizationFlu:    "",
    immunizationPneumo: "",
    immunizationZoster: "",
    screeningsPlanText: "",

    // Screening results
    breastCancerScreen:   "",
    colorectalScreen:     "",
    papScreen:            "",
    echoNuclearEF:        "",

    // Labs
    hemoglobinA1C:  "",
    cholesterol:    "",
    hdl:            "",
    ldl:            "",
    tsh:            "",

    // Vitals
    bpSystolic:       "",
    bpDiastolic:      "",
    weight:           "",
    height:           "",
    bmi:              "",
    oxygenSaturation: "",
    homeOxygenOrCPAP: "",
    homeOxygenDetail: "",

    // Sig block
    responsibleParty: patientName,
    relationship:     "Self",
    sigDate:          today,
  });

  const set    = (k, v)      => setForm(f => ({ ...f, [k]: v }));
  const setAdl = (n, field, v) => setForm(f => ({
    ...f,
    adl: { ...f.adl, [`q${n}`]: { ...f.adl[`q${n}`], [field]: v } },
  }));

  const getSig = () =>
    (!sigRef.current || sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType: "wellness_assessment",
    patient:  { name: patientName, age: patientAge !== "" ? Number(patientAge) : null, gender: patientGender },
    phq9: {
      date:  form.phq9Date || null,
      score: form.phq9Score !== "" ? Number(form.phq9Score) : null,
      level: form.phq9Level,
    },
    cognitive: {
      alert:           form.cogAlert,
      orientedToPlace: form.cogOrientedPlace,
      orientedToTime:  form.cogOrientedTime,
      orientedToPerson:form.cogOrientedPerson,
    },
    appearance: {
      noDistress:    form.appearNoDistress,
      affectNormal:  form.appearAffectNormal,
      functionalAbility: form.appearFunctional,
      levelOfSafety: form.appearLevelOfSafety,
    },
    hearing: {
      impaired:      form.hearingImpaired,
      usesHearingAid:form.usesHearingAid,
    },
    fallRisk: {
      fallsLastYear:    form.fallsLastYear !== "" ? Number(form.fallsLastYear) : null,
      afraidOfFalling:  form.afraidOfFalling,
    },
    transfers: {
      independent: form.transfersIndependent,
      explain:     form.transfersExplain,
    },
    homeSafety: {
      rugPresent:   form.homeSafetyRug,
      barsInBathroom: form.homeSafetyBathroomBars,
      barOnStairs:  form.homeSafetyStairBars,
    },
    adlItems: Object.fromEntries(
      ADL_ITEMS.map(([n, label]) => [label, form.adl[`q${n}`]])
    ),
    generalHealth: {
      painScreening:      form.painScreening,
      exerciseLevel:      form.exerciseLevel,
      exerciseDaysPerWeek:form.exerciseDaysPerWeek !== "" ? Number(form.exerciseDaysPerWeek) : null,
      stressLevel:        form.stressLevel,
      tobaccoUse:         form.tobaccoUse,
      secondhandSmokeOnly:form.secondhandSmoke,
      alcoholMisuse:      form.alcoholMisuse,
      alcoholDetail:      form.alcoholDetail,
    },
    preventiveCare: {
      dailyAspirin:       form.dailyAspirin,
      immunizations: {
        flu:           form.immunizationFlu,
        pneumococcal:  form.immunizationPneumo,
        zoster:        form.immunizationZoster,
      },
      screeningsPlan:    form.screeningsPlanText,
      screeningResults: {
        breastCancer:  form.breastCancerScreen,
        colorectal:    form.colorectalScreen,
        pap:           form.papScreen,
        echoNuclearEF: form.echoNuclearEF,
      },
      labs: {
        hemoglobinA1C: form.hemoglobinA1C,
        cholesterol:   form.cholesterol,
        hdl:           form.hdl,
        ldl:           form.ldl,
        tsh:           form.tsh,
      },
      vitals: {
        bpSystolic:       form.bpSystolic !== "" ? Number(form.bpSystolic) : null,
        bpDiastolic:      form.bpDiastolic !== "" ? Number(form.bpDiastolic) : null,
        weight:           form.weight,
        height:           form.height,
        bmi:              form.bmi,
        oxygenSaturation: form.oxygenSaturation,
        homeOxygenOrCPAP: form.homeOxygenOrCPAP,
        homeOxygenDetail: form.homeOxygenDetail,
      },
    },
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion: "1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.", "error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit === "function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting to dashboard…", "success");
      setTimeout(() => history.push(NEXT), 1800);
    } catch (e) { console.error(e); showToast("Save failed.", "error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef, `wellness-assessment-${patientName || "patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.", "error"); }
  };

  const YN = ({ name, value, onChange }) => (
    <div className="wf-radio-group">
      {["Yes", "No"].map(o => (
        <label className="wf-radio-label" key={o}>
          <input type="radio" name={name} value={o} checked={value === o} onChange={e => onChange(e.target.value)} />{o}
        </label>
      ))}
    </div>
  );

  const ST = ({ num, label }) => (
    <div className="wf-section-title">
      <span className="num" style={{ fontSize: 11, width: 26, height: 26 }}>{num}</span>{label}
    </div>
  );

  return (
    <FormShell
      title="Annual Wellness / Preventive Visit"
      subtitle="Please complete the areas you know. This helps us address your health issues and plan preventive care for the coming year."
      dbLabel="MongoDB · wellness_assessments"
      patientName={patientName}
      patientAge={patientAge}
      patientGender={patientGender}
      savedId={savedId}
      formRef={formRef}
      onSubmit={handleSubmit}
      onDownload={handleDownload}
      submitting={submitting}
    >

      {/* ── 1. PHQ-9 ── */}
      <div className="wf-section">
        <ST num="1" label="Depression Screen — PHQ-9" />
        <div className="wf-grid wf-grid-3" style={{ marginBottom: 16 }}>
          <Field label="PHQ-9 Date">
            <input type="date" value={form.phq9Date} onChange={e => set("phq9Date", e.target.value)} />
          </Field>
          <Field label="Score (0–27)">
            <input type="number" min={0} max={27} value={form.phq9Score}
              onChange={e => set("phq9Score", e.target.value)} placeholder="Enter score" />
          </Field>
        </div>
        <Field label="Depression Level (select one)">
          <div className="wf-radio-group">
            {DEPRESSION_LEVELS.map(lv => (
              <label className="wf-radio-label" key={lv}>
                <input type="radio" name="phq9Level" value={lv}
                  checked={form.phq9Level === lv} onChange={e => set("phq9Level", e.target.value)} />{lv}
              </label>
            ))}
          </div>
        </Field>

        {/* 1b. Cognitive */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 10 }}>
            1b. Cognitive Impairment Detection
          </div>
          <div className="wf-grid wf-grid-4">
            {[
              ["cogAlert",          "Alert"],
              ["cogOrientedPlace",  "Oriented to Place"],
              ["cogOrientedTime",   "Oriented to Time"],
              ["cogOrientedPerson", "Oriented to Person"],
            ].map(([k, lbl]) => (
              <Field key={k} label={lbl}>
                <input type="text" value={form[k]} onChange={e => set(k, e.target.value)}
                  placeholder="Notes…" />
              </Field>
            ))}
          </div>
        </div>

        {/* 1c. Appearance */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 10 }}>
            1c. Appearance
          </div>
          <div className="wf-grid wf-grid-4">
            {[
              ["appearNoDistress",    "No Distress"],
              ["appearAffectNormal",  "Affect Normal"],
              ["appearFunctional",    "Functional Ability"],
              ["appearLevelOfSafety", "Level of Safety"],
            ].map(([k, lbl]) => (
              <Field key={k} label={lbl}>
                <input type="text" value={form[k]} onChange={e => set(k, e.target.value)}
                  placeholder="Notes…" />
              </Field>
            ))}
          </div>
        </div>
      </div>

      {/* ── 2. Hearing ── */}
      <div className="wf-section">
        <ST num="2" label="Hearing" />
        <div className="wf-grid wf-grid-2">
          <Field label="Hearing Impaired?">
            <YN name="hearingImpaired" value={form.hearingImpaired} onChange={v => set("hearingImpaired", v)} />
          </Field>
          <Field label="Uses Hearing Aid?">
            <YN name="usesHearingAid" value={form.usesHearingAid} onChange={v => set("usesHearingAid", v)} />
          </Field>
        </div>
      </div>

      {/* ── 3. Fall Risk ── */}
      <div className="wf-section">
        <ST num="3" label="Fall Risk" />
        <div className="wf-grid wf-grid-2">
          <Field label="# of Falls in Last Year">
            <input type="number" min={0} value={form.fallsLastYear}
              onChange={e => set("fallsLastYear", e.target.value)} placeholder="Number of falls" />
          </Field>
          <Field label="Afraid of Falling?">
            <YN name="afraidFalling" value={form.afraidOfFalling} onChange={v => set("afraidOfFalling", v)} />
          </Field>
        </div>
      </div>

      {/* ── 4. Transfers ── */}
      <div className="wf-section">
        <ST num="4" label="Transfers — Bed to Chair Without Assistance" />
        <Field label="Can transfer independently?">
          <YN name="transfers" value={form.transfersIndependent} onChange={v => set("transfersIndependent", v)} />
        </Field>
        {form.transfersIndependent === "No" && (
          <Field label="If no, explain" style={{ marginTop: 12 }}>
            <textarea value={form.transfersExplain} onChange={e => set("transfersExplain", e.target.value)}
              placeholder="Explain limitations…" style={{ minHeight: 60 }} />
          </Field>
        )}
      </div>

      {/* ── 5. Home Safety ── */}
      <div className="wf-section">
        <ST num="5" label="Home Safety" />
        <div className="wf-grid wf-grid-3">
          {[
            ["homeSafetyRug",          "Rug Present?"],
            ["homeSafetyBathroomBars", "Bars in Bathroom?"],
            ["homeSafetyStairBars",    "Bar on Stairs?"],
          ].map(([k, lbl]) => (
            <Field key={k} label={lbl}>
              <YN name={k} value={form[k]} onChange={v => set(k, v)} />
            </Field>
          ))}
        </div>
      </div>

      {/* ── 6–11. ADLs ── */}
      <div className="wf-section">
        <ST num="6–11" label="Activities of Daily Living (ADLs)" />
        {ADL_ITEMS.map(([n, lbl]) => (
          <div key={n} style={{
            marginBottom: 14, padding: "12px 16px",
            border: `1.5px solid ${C.border}`, borderRadius: 10,
            background: form.adl[`q${n}`]?.answer === "Yes" ? "#FEF3C7" : C.white,
          }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>{n}. {lbl}</div>
            <YN name={`adl_${n}`} value={form.adl[`q${n}`]?.answer}
              onChange={v => setAdl(n, "answer", v)} />
            {(form.adl[`q${n}`]?.answer === "Yes" || form.adl[`q${n}`]?.answer === "No") && (
              <Field label="Explain (if applicable)" style={{ marginTop: 10 }}>
                <input type="text" value={form.adl[`q${n}`]?.explain || ""}
                  onChange={e => setAdl(n, "explain", e.target.value)}
                  placeholder="Additional notes…" />
              </Field>
            )}
          </div>
        ))}
      </div>

      {/* ── 12. General Health ── */}
      <div className="wf-section">
        <ST num="12" label="General Health" />

        {/* Pain */}
        <Field label="12a. Pain Screening" style={{ marginBottom: 14 }}>
          <input type="text" value={form.painScreening} onChange={e => set("painScreening", e.target.value)}
            placeholder="Describe pain screening results…" />
        </Field>

        {/* Exercise */}
        <Field label="12b. Exercise Level" style={{ marginBottom: 8 }}>
          <div className="wf-radio-group">
            {["None", "Mild", "Moderate"].map(o => (
              <label className="wf-radio-label" key={o}>
                <input type="radio" name="exerciseLevel" value={o}
                  checked={form.exerciseLevel === o} onChange={e => set("exerciseLevel", e.target.value)} />{o}
              </label>
            ))}
          </div>
        </Field>
        {form.exerciseLevel === "Moderate" && (
          <Field label="Days per week" style={{ maxWidth: 180, marginBottom: 14 }}>
            <input type="number" min={1} max={7} value={form.exerciseDaysPerWeek}
              onChange={e => set("exerciseDaysPerWeek", e.target.value)} />
          </Field>
        )}

        {/* Stress */}
        <Field label="12c. Stress Level" style={{ marginBottom: 14 }}>
          <div className="wf-radio-group">
            {["Mild", "Moderate", "Severe"].map(o => (
              <label className="wf-radio-label" key={o}>
                <input type="radio" name="stressLevel" value={o}
                  checked={form.stressLevel === o} onChange={e => set("stressLevel", e.target.value)} />{o}
              </label>
            ))}
          </div>
        </Field>

        {/* Tobacco */}
        <div className="wf-grid wf-grid-2" style={{ marginBottom: 14 }}>
          <Field label="12d. Tobacco Use?">
            <YN name="tobaccoUse" value={form.tobaccoUse} onChange={v => set("tobaccoUse", v)} />
            <label style={{ display: "flex", gap: 8, marginTop: 8, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" style={{ accentColor: C.teal }}
                checked={form.secondhandSmoke} onChange={e => set("secondhandSmoke", e.target.checked)} />
              Second-hand smoke exposure only
            </label>
          </Field>

          {/* Alcohol */}
          <Field label="12e. Alcohol Misuse?">
            <YN name="alcoholMisuse" value={form.alcoholMisuse} onChange={v => set("alcoholMisuse", v)} />
            {form.alcoholMisuse === "Yes" && (
              <input type="text" value={form.alcoholDetail} onChange={e => set("alcoholDetail", e.target.value)}
                placeholder="Details…" style={{ marginTop: 8 }} />
            )}
          </Field>
        </div>
      </div>

      {/* ── Preventive Care ── */}
      <div className="wf-section">
        <ST num="Prev." label="Preventive Care &amp; Immunizations" />
        <div className="wf-grid wf-grid-4" style={{ marginBottom: 14 }}>
          <Field label="Daily Aspirin">
            <input type="text" value={form.dailyAspirin} onChange={e => set("dailyAspirin", e.target.value)}
              placeholder="Yes / No / Dose" />
          </Field>
          <Field label="Flu Vaccine">
            <input type="text" value={form.immunizationFlu} onChange={e => set("immunizationFlu", e.target.value)}
              placeholder="Date or status" />
          </Field>
          <Field label="Pneumococcal Vaccine">
            <input type="text" value={form.immunizationPneumo} onChange={e => set("immunizationPneumo", e.target.value)}
              placeholder="Date or status" />
          </Field>
          <Field label="Zoster Vaccine">
            <input type="text" value={form.immunizationZoster} onChange={e => set("immunizationZoster", e.target.value)}
              placeholder="Date or status" />
          </Field>
        </div>

        <Field label="Screenings Done or Planned" style={{ marginBottom: 14 }}>
          <input type="text" value={form.screeningsPlanText} onChange={e => set("screeningsPlanText", e.target.value)}
            placeholder="List any screenings done or scheduled…" />
        </Field>

        <div className="wf-grid wf-grid-4" style={{ marginBottom: 14 }}>
          {[
            ["breastCancerScreen", "Breast Cancer Screen"],
            ["colorectalScreen",   "Colorectal Cancer"],
            ["papScreen",          "Pap Smear"],
            ["echoNuclearEF",      "Echo / Nuclear EF"],
          ].map(([k, lbl]) => (
            <Field key={k} label={lbl}>
              <input type="text" value={form[k]} onChange={e => set(k, e.target.value)}
                placeholder="Result / date" />
            </Field>
          ))}
        </div>

        {/* Labs */}
        <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 10 }}>Blood Tests</div>
        <div className="wf-grid wf-grid-4" style={{ marginBottom: 14 }}>
          {[
            ["hemoglobinA1C", "Hemoglobin A1C"],
            ["cholesterol",   "Cholesterol"],
            ["hdl",           "HDL"],
            ["ldl",           "LDL"],
            ["tsh",           "TSH"],
          ].map(([k, lbl]) => (
            <Field key={k} label={lbl}>
              <input type="text" value={form[k]} onChange={e => set(k, e.target.value)}
                placeholder="Value" />
            </Field>
          ))}
        </div>

        {/* Vitals */}
        <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 10 }}>Vitals</div>
        <div className="wf-grid wf-grid-4">
          <Field label="BP Systolic (mmHg)">
            <input type="number" value={form.bpSystolic} onChange={e => set("bpSystolic", e.target.value)} placeholder="e.g. 120" />
          </Field>
          <Field label="BP Diastolic (mmHg)">
            <input type="number" value={form.bpDiastolic} onChange={e => set("bpDiastolic", e.target.value)} placeholder="e.g. 80" />
          </Field>
          <Field label="Weight (lbs)">
            <input type="text" value={form.weight} onChange={e => set("weight", e.target.value)} />
          </Field>
          <Field label="Height">
            <input type="text" value={form.height} onChange={e => set("height", e.target.value)} placeholder='e.g. 5ft 8ins' />
          </Field>
          <Field label="BMI">
            <input type="text" value={form.bmi} onChange={e => set("bmi", e.target.value)} />
          </Field>
          <Field label="Oxygen Saturation (%)">
            <input type="number" min={50} max={100} value={form.oxygenSaturation}
              onChange={e => set("oxygenSaturation", e.target.value)} />
          </Field>
          <Field label="On Home Oxygen or CPAP?">
            <YN name="homeO2" value={form.homeOxygenOrCPAP} onChange={v => set("homeOxygenOrCPAP", v)} />
          </Field>
          {form.homeOxygenOrCPAP === "Yes" && (
            <Field label="Details">
              <input type="text" value={form.homeOxygenDetail} onChange={e => set("homeOxygenDetail", e.target.value)}
                placeholder="Device / L-flow / settings" />
            </Field>
          )}
        </div>
      </div>

      <SigBlock
        sigRef={sigRef}
        sigEmpty={sigEmpty}
        setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty}
        setResponsibleParty={v => set("responsibleParty", v)}
        relationship={form.relationship}
        setRelationship={v => set("relationship", v)}
        sigDate={form.sigDate}
        setSigDate={v => set("sigDate", v)}
      />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const WellnessAssessmentSchema = new Schema({
  formType:  { type: String, default: "wellness_assessment" },
  patient:   { name: String, age: Number, gender: String },
  phq9:      { date: Date, score: Number, level: String },
  cognitive: { alert: String, orientedToPlace: String, orientedToTime: String, orientedToPerson: String },
  appearance: { noDistress: String, affectNormal: String, functionalAbility: String, levelOfSafety: String },
  hearing:   { impaired: String, usesHearingAid: String },
  fallRisk:  { fallsLastYear: Number, afraidOfFalling: String },
  transfers: { independent: String, explain: String },
  homeSafety: { rugPresent: String, barsInBathroom: String, barOnStairs: String },
  adlItems:  Schema.Types.Mixed,
  generalHealth: Schema.Types.Mixed,
  preventiveCare: Schema.Types.Mixed,
  authorization: { responsibleParty: String, relationship: String, sigDate: Date, signatureDataURL: String },
  meta: { submittedAt: Date, formVersion: String },
}, { timestamps: true, collection: "wellness_assessments" });

── Express route ──────────────────────────────────────────────────────────────
router.post('/', async (req, res) => {
  const doc = new WellnessModel(req.body);
  await doc.save();
  res.status(201).json({ _id: doc._id, createdAt: doc.createdAt });
});
router.get('/:id', async (req, res) => {
  const doc = await WellnessModel.findById(req.params.id).lean();
  res.json(doc);
});
// Mount at: /api/forms/wellness-assessment
*/
